import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Loader2, Network, Coins, Plus, Pencil, Trash2, ExternalLink, Power,
  GripVertical, CheckCircle2, XCircle, Zap, Globe, ArrowUp, ArrowDown, FlaskConical,
  Radio, RefreshCw, Activity
} from 'lucide-react';

const AdminNetworks = () => {
  const [networks, setNetworks] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [listenerStatuses, setListenerStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [networkDialogOpen, setNetworkDialogOpen] = useState(false);
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [editingNetwork, setEditingNetwork] = useState(null);
  const [editingToken, setEditingToken] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testingEndpoint, setTestingEndpoint] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [restartingNetwork, setRestartingNetwork] = useState(null);

  const emptyNetworkForm = {
    id: '', name: '', symbol: '', chain_id: '', explorer_url: '', confirmations_required: 12,
    rpc_url: '', wss_url: '', websocket_type: 'HTTPS',
    endpoints: []
  };

  const [networkForm, setNetworkForm] = useState(emptyNetworkForm);
  const [tokenForm, setTokenForm] = useState({ network_id: '', name: '', symbol: '', contract_address: '', decimals: 18 });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [networksRes, tokensRes, statusRes] = await Promise.all([
        axios.get(`${API}/networks`),
        axios.get(`${API}/networks/all-tokens`),
        axios.get(`${API}/blockchain/status/all`).catch(() => ({ data: [] }))
      ]);
      setNetworks(networksRes.data);
      setTokens(tokensRes.data);
      setListenerStatuses(statusRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getListenerStatus = (networkId) => listenerStatuses.find(s => s.network_id === networkId);

  const syncListeners = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/blockchain/sync`);
      setListenerStatuses(res.data.statuses || []);
      toast.success('Listeners synced successfully');
    } catch (error) {
      toast.error('Failed to sync listeners');
    } finally {
      setSyncing(false);
    }
  };

  const restartListener = async (networkId) => {
    setRestartingNetwork(networkId);
    try {
      await axios.post(`${API}/blockchain/restart/${networkId}`);
      toast.success(`Listener for ${networkId} restarted`);
      // Refresh statuses after a brief delay
      setTimeout(async () => {
        try {
          const res = await axios.get(`${API}/blockchain/status/all`);
          setListenerStatuses(res.data || []);
        } catch {}
        setRestartingNetwork(null);
      }, 3000);
    } catch (error) {
      toast.error('Failed to restart listener');
      setRestartingNetwork(null);
    }
  };

  // ---- Network CRUD ----
  const openNetworkDialog = (network = null) => {
    if (network) {
      setEditingNetwork(network);
      const endpoints = network.endpoints || [];
      // Migrate old single-URL to endpoints if empty
      const migrated = endpoints.length > 0 ? endpoints : [];
      if (migrated.length === 0) {
        if (network.wss_url) migrated.push({ url: network.wss_url, type: 'wss', label: 'Primary WSS', priority: 1 });
        if (network.rpc_url) migrated.push({ url: network.rpc_url, type: 'https', label: 'Primary RPC', priority: migrated.length + 1 });
      }
      setNetworkForm({
        id: network.id, name: network.name, symbol: network.symbol,
        chain_id: network.chain_id, explorer_url: network.explorer_url,
        confirmations_required: network.confirmations_required || 12,
        rpc_url: network.rpc_url || '', wss_url: network.wss_url || '',
        websocket_type: network.websocket_type || 'HTTPS',
        endpoints: migrated.map((e, i) => ({ ...e, _key: `ep-${i}-${Date.now()}` }))
      });
    } else {
      setEditingNetwork(null);
      setNetworkForm({ ...emptyNetworkForm, endpoints: [] });
    }
    setNetworkDialogOpen(true);
  };

  const addEndpoint = (type) => {
    const maxPriority = networkForm.endpoints.reduce((max, e) => Math.max(max, e.priority || 0), 0);
    setNetworkForm({
      ...networkForm,
      endpoints: [...networkForm.endpoints, {
        url: '', type, label: '', priority: maxPriority + 1, _key: `ep-${Date.now()}`
      }]
    });
  };

  const updateEndpoint = (key, field, value) => {
    setNetworkForm({
      ...networkForm,
      endpoints: networkForm.endpoints.map(e => e._key === key ? { ...e, [field]: value } : e)
    });
  };

  const removeEndpoint = (key) => {
    setNetworkForm({
      ...networkForm,
      endpoints: networkForm.endpoints.filter(e => e._key !== key)
    });
  };

  const moveEndpoint = (key, direction) => {
    const eps = [...networkForm.endpoints];
    const idx = eps.findIndex(e => e._key === key);
    if (direction === 'up' && idx > 0) {
      [eps[idx], eps[idx - 1]] = [eps[idx - 1], eps[idx]];
    } else if (direction === 'down' && idx < eps.length - 1) {
      [eps[idx], eps[idx + 1]] = [eps[idx + 1], eps[idx]];
    }
    // Re-assign priorities
    eps.forEach((e, i) => { e.priority = i + 1; });
    setNetworkForm({ ...networkForm, endpoints: eps });
  };

  const testEndpoint = async (ep) => {
    setTestingEndpoint(ep._key);
    try {
      const { data } = await axios.post(`${API}/admin/networks/test-endpoint`, { url: ep.url, type: ep.type });
      if (data.ok) {
        toast.success(`Endpoint OK — Block #${data.block.toLocaleString()}, ${data.latency_ms}ms${data.subscriptions ? ', Subscriptions OK' : ''}`);
      } else {
        toast.error(`Endpoint failed: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      toast.error('Test failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTestingEndpoint(null);
    }
  };

  const saveNetwork = async (e) => {
    e.preventDefault();
    // Validate endpoints
    for (const ep of networkForm.endpoints) {
      if (!ep.url) { toast.error('All endpoints must have a URL'); return; }
      if (ep.type === 'wss' && !ep.url.startsWith('wss://')) { toast.error(`WSS URL must start with wss://`); return; }
      if (ep.type === 'https' && !ep.url.startsWith('https://')) { toast.error(`HTTPS URL must start with https://`); return; }
    }
    setSaving(true);
    try {
      const payload = {
        ...networkForm,
        chain_id: parseInt(networkForm.chain_id),
        confirmations_required: parseInt(networkForm.confirmations_required),
        endpoints: networkForm.endpoints.map(({ _key, ...rest }) => rest)
      };
      if (editingNetwork) {
        await axios.put(`${API}/admin/networks/${editingNetwork.id}`, payload);
        toast.success('Network updated!');
      } else {
        await axios.post(`${API}/admin/networks`, payload);
        toast.success('Network created!');
      }
      setNetworkDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save network');
    } finally {
      setSaving(false);
    }
  };

  const deleteNetwork = async (networkId) => {
    if (!confirm('Delete this network?')) return;
    try {
      await axios.delete(`${API}/admin/networks/${networkId}`);
      toast.success('Network deleted');
      fetchData();
    } catch (error) { toast.error('Failed to delete network'); }
  };

  const toggleNetwork = async (networkId) => {
    try {
      const response = await axios.patch(`${API}/admin/networks/${networkId}/toggle`);
      toast.success(`Network ${response.data.is_active ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (error) { toast.error('Failed to toggle network'); }
  };

  // ---- Token CRUD ----
  const openTokenDialog = (token = null) => {
    if (token) {
      setEditingToken(token);
      setTokenForm({ network_id: token.network_id, name: token.name, symbol: token.symbol, contract_address: token.contract_address, decimals: token.decimals });
    } else {
      setEditingToken(null);
      setTokenForm({ network_id: networks[0]?.id || 'bsc', name: '', symbol: '', contract_address: '', decimals: 18 });
    }
    setTokenDialogOpen(true);
  };

  const saveToken = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...tokenForm, decimals: parseInt(tokenForm.decimals) };
      if (editingToken) {
        await axios.put(`${API}/admin/tokens/${editingToken.id}`, payload);
        toast.success('Token updated!');
      } else {
        await axios.post(`${API}/admin/tokens`, payload);
        toast.success('Token created!');
      }
      setTokenDialogOpen(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save token'); }
    finally { setSaving(false); }
  };

  const deleteToken = async (tokenId) => {
    if (!confirm('Delete this token?')) return;
    try { await axios.delete(`${API}/admin/tokens/${tokenId}`); toast.success('Token deleted'); fetchData(); }
    catch { toast.error('Failed to delete token'); }
  };

  const toggleToken = async (tokenId) => {
    try {
      const response = await axios.patch(`${API}/admin/tokens/${tokenId}/toggle`);
      toast.success(`Token ${response.data.is_active ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch { toast.error('Failed to toggle token'); }
  };

  const getNetworkName = (networkId) => networks.find(n => n.id === networkId)?.name || networkId;

  return (
    <AdminLayout title="Networks & Tokens">
      <div className="space-y-6" data-testid="admin-networks-page">
        <Tabs defaultValue="networks" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="networks" data-testid="networks-tab">Networks</TabsTrigger>
            <TabsTrigger value="tokens" data-testid="tokens-tab">Tokens</TabsTrigger>
          </TabsList>

          {/* Networks Tab */}
          <TabsContent value="networks">
            <div className="flex items-center justify-between pb-6">
              <p className="text-muted-foreground">Manage supported blockchain networks</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={syncListeners} disabled={syncing} data-testid="sync-listeners-btn">
                  {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                  Sync Listeners
                </Button>
                <Dialog open={networkDialogOpen} onOpenChange={setNetworkDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-primary text-white" onClick={() => openNetworkDialog()} data-testid="add-network-btn">
                      <Plus className="h-4 w-4 mr-2" /> Add Network
                    </Button>
                  </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingNetwork ? 'Edit Network' : 'Add Network'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={saveNetwork} className="space-y-4">
                    {/* Basic info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Network ID</Label>
                        <Input placeholder="bsc, eth..." value={networkForm.id} onChange={(e) => setNetworkForm({ ...networkForm, id: e.target.value.toLowerCase() })} required disabled={!!editingNetwork} data-testid="network-id-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Chain ID</Label>
                        <Input type="number" placeholder="56" value={networkForm.chain_id} onChange={(e) => setNetworkForm({ ...networkForm, chain_id: e.target.value })} required data-testid="network-chain-id-input" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Network Name</Label>
                        <Input placeholder="BNB Smart Chain" value={networkForm.name} onChange={(e) => setNetworkForm({ ...networkForm, name: e.target.value })} required data-testid="network-name-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Native Symbol</Label>
                        <Input placeholder="BNB" value={networkForm.symbol} onChange={(e) => setNetworkForm({ ...networkForm, symbol: e.target.value.toUpperCase() })} required data-testid="network-symbol-input" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Explorer URL</Label>
                        <Input placeholder="https://bscscan.com" value={networkForm.explorer_url} onChange={(e) => setNetworkForm({ ...networkForm, explorer_url: e.target.value })} required data-testid="network-explorer-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Confirmations Required</Label>
                        <Input type="number" placeholder="12" value={networkForm.confirmations_required} onChange={(e) => setNetworkForm({ ...networkForm, confirmations_required: e.target.value })} required />
                      </div>
                    </div>

                    {/* Endpoints Section */}
                    <div className="space-y-3 pt-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-base font-semibold">Endpoints</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">Add multiple RPC and WebSocket endpoints. Higher priority endpoints are tried first with automatic failover.</p>
                        </div>
                      </div>

                      {/* Endpoint List */}
                      <div className="space-y-2">
                        {networkForm.endpoints.map((ep, idx) => (
                          <div
                            key={ep._key}
                            className="flex items-center gap-2 p-3 bg-muted/30 border border-gray-200 dark:border-white/10 rounded-lg"
                            data-testid={`endpoint-row-${idx}`}
                          >
                            <div className="flex flex-col gap-0.5">
                              <button type="button" onClick={() => moveEndpoint(ep._key, 'up')} disabled={idx === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors" data-testid={`endpoint-up-${idx}`}>
                                <ArrowUp className="h-3 w-3" />
                              </button>
                              <button type="button" onClick={() => moveEndpoint(ep._key, 'down')} disabled={idx === networkForm.endpoints.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors" data-testid={`endpoint-down-${idx}`}>
                                <ArrowDown className="h-3 w-3" />
                              </button>
                            </div>
                            <span className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                              ep.type === 'wss' ? 'bg-violet-500/15 text-violet-500' : 'bg-blue-500/15 text-blue-500'
                            }`}>
                              {ep.type === 'wss' ? <><Zap className="h-2.5 w-2.5 inline mr-0.5" />WSS</> : <><Globe className="h-2.5 w-2.5 inline mr-0.5" />RPC</>}
                            </span>
                            <Input
                              placeholder="Label (optional)"
                              value={ep.label || ''}
                              onChange={(e) => updateEndpoint(ep._key, 'label', e.target.value)}
                              className="w-28 h-8 text-xs"
                              data-testid={`endpoint-label-${idx}`}
                            />
                            <Input
                              placeholder={ep.type === 'wss' ? 'wss://...' : 'https://...'}
                              value={ep.url}
                              onChange={(e) => updateEndpoint(ep._key, 'url', e.target.value)}
                              className="flex-1 h-8 text-xs font-mono"
                              required
                              data-testid={`endpoint-url-${idx}`}
                            />
                            <button
                              type="button"
                              onClick={() => testEndpoint(ep)}
                              disabled={!ep.url || testingEndpoint === ep._key}
                              className="shrink-0 h-8 px-2 rounded-md bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors disabled:opacity-40 flex items-center gap-1"
                              data-testid={`endpoint-test-${idx}`}
                            >
                              {testingEndpoint === ep._key ? <Loader2 className="h-3 w-3 animate-spin" /> : <FlaskConical className="h-3 w-3" />}
                              Test
                            </button>
                            <button
                              type="button"
                              onClick={() => removeEndpoint(ep._key)}
                              className="shrink-0 h-8 w-8 rounded-md text-destructive hover:bg-destructive/10 transition-colors flex items-center justify-center"
                              data-testid={`endpoint-remove-${idx}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}

                        {networkForm.endpoints.length === 0 && (
                          <div className="text-center py-6 text-muted-foreground border border-dashed border-gray-200 dark:border-white/10 rounded-lg">
                            <Network className="h-8 w-8 mx-auto mb-2 opacity-40" />
                            <p className="text-sm">No endpoints configured. Add WSS or HTTPS endpoints below.</p>
                          </div>
                        )}
                      </div>

                      {/* Add Endpoint Buttons */}
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => addEndpoint('wss')} data-testid="add-wss-endpoint-btn" className="text-xs">
                          <Zap className="h-3 w-3 mr-1.5 text-violet-500" /> Add WSS Endpoint
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => addEndpoint('https')} data-testid="add-https-endpoint-btn" className="text-xs">
                          <Globe className="h-3 w-3 mr-1.5 text-blue-500" /> Add HTTPS Endpoint
                        </Button>
                      </div>
                    </div>

                    <Button type="submit" className="w-full bg-primary text-white" disabled={saving} data-testid="save-network-btn">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingNetwork ? 'Update Network' : 'Add Network'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : (
              <Card className="border-border bg-card">
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    {networks.map((network) => {
                      const eps = network.endpoints || [];
                      const wssCount = eps.filter(e => e.type === 'wss').length;
                      const httpsCount = eps.filter(e => e.type === 'https').length;
                      const lStatus = getListenerStatus(network.id);
                      return (
                        <div key={network.id} className="p-4 bg-card border border-gray-200 dark:border-white/10 rounded-lg shadow-sm" data-testid={`network-row-${network.id}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                                <Network className="h-5 w-5 text-yellow-500" />
                              </div>
                              <div>
                                <p className="font-medium">{network.name}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-xs text-muted-foreground">Chain {network.chain_id}</span>
                                  <span className="text-xs text-muted-foreground">|</span>
                                  <span className="text-xs text-muted-foreground">{network.confirmations_required} confs</span>
                                  {eps.length > 0 && (
                                    <>
                                      <span className="text-xs text-muted-foreground">|</span>
                                      <span className="flex items-center gap-1.5">
                                        {wssCount > 0 && (
                                          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-500">
                                            <Zap className="h-2.5 w-2.5" />{wssCount} WSS
                                          </span>
                                        )}
                                        {httpsCount > 0 && (
                                          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-500">
                                            <Globe className="h-2.5 w-2.5" />{httpsCount} RPC
                                          </span>
                                        )}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {/* Listener status indicator */}
                              {lStatus ? (
                                <div className="flex items-center gap-2" data-testid={`listener-status-${network.id}`}>
                                  <div className={`w-2 h-2 rounded-full ${
                                    lStatus.connected ? 'bg-emerald-400 animate-pulse'
                                    : lStatus.is_dormant ? 'bg-amber-400'
                                    : 'bg-red-400'
                                  }`} />
                                  <div className="text-right">
                                    <span className={`text-xs font-medium ${
                                      lStatus.connected ? 'text-emerald-500'
                                      : lStatus.is_dormant ? 'text-amber-500'
                                      : 'text-red-400'
                                    }`}>
                                      {lStatus.connected
                                        ? lStatus.mode.toUpperCase()
                                        : lStatus.is_dormant
                                          ? (lStatus.chain_type === 'tron' ? 'Dormant (non-EVM)' : 'Standby')
                                          : 'Disconnected'}
                                    </span>
                                    {lStatus.connected && (
                                      <p className="text-[10px] text-muted-foreground">
                                        Block #{lStatus.current_block.toLocaleString()} | {lStatus.monitored_wallets}w / {lStatus.monitored_tokens}t
                                      </p>
                                    )}
                                    {lStatus.is_dormant && !lStatus.connected && (
                                      <p className="text-[10px] text-muted-foreground">
                                        {lStatus.chain_type === 'tron' ? 'EVM listener not compatible' : 'No pending invoices — auto-resumes'}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ) : network.is_active !== false ? (
                                <span className="text-[10px] text-muted-foreground/50">No listener</span>
                              ) : null}
                              <Badge className={network.is_active !== false ? 'bg-emerald-500/20 text-emerald-500' : 'bg-muted text-muted-foreground'}>
                                {network.is_active !== false ? 'Active' : 'Inactive'}
                              </Badge>
                              {lStatus && (
                                <Button size="icon" variant="ghost" onClick={() => restartListener(network.id)} disabled={restartingNetwork === network.id} data-testid={`restart-listener-${network.id}`} title="Restart Listener">
                                  {restartingNetwork === network.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 text-muted-foreground" />}
                                </Button>
                              )}
                              <Button size="icon" variant="ghost" onClick={() => toggleNetwork(network.id)} data-testid={`toggle-network-${network.id}`}>
                                <Power className={`h-4 w-4 ${network.is_active !== false ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => openNetworkDialog(network)} data-testid={`edit-network-${network.id}`}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deleteNetwork(network.id)} data-testid={`delete-network-${network.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              {network.explorer_url && (
                                <a href={network.explorer_url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {networks.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <Network className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No networks configured</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tokens Tab */}
          <TabsContent value="tokens">
            <div className="flex items-center justify-between pb-6">
              <p className="text-muted-foreground">Manage supported tokens/currencies</p>
              <Dialog open={tokenDialogOpen} onOpenChange={setTokenDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-white" onClick={() => openTokenDialog()} data-testid="add-token-btn">
                    <Plus className="h-4 w-4 mr-2" /> Add Token
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{editingToken ? 'Edit Token' : 'Add Token'}</DialogTitle></DialogHeader>
                  <form onSubmit={saveToken} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Network</Label>
                      <Select value={tokenForm.network_id} onValueChange={(v) => setTokenForm({ ...tokenForm, network_id: v })} disabled={!!editingToken}>
                        <SelectTrigger data-testid="token-network-select"><SelectValue placeholder="Select network" /></SelectTrigger>
                        <SelectContent>
                          {networks.filter(n => n.is_active !== false).map((network) => (
                            <SelectItem key={network.id} value={network.id}>{network.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Token Name</Label>
                        <Input placeholder="Tether USD" value={tokenForm.name} onChange={(e) => setTokenForm({ ...tokenForm, name: e.target.value })} required data-testid="token-name-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Symbol</Label>
                        <Input placeholder="USDT" value={tokenForm.symbol} onChange={(e) => setTokenForm({ ...tokenForm, symbol: e.target.value.toUpperCase() })} required data-testid="token-symbol-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Contract Address</Label>
                      <Input placeholder="0x... or 'native'" value={tokenForm.contract_address} onChange={(e) => setTokenForm({ ...tokenForm, contract_address: e.target.value })} required data-testid="token-contract-input" />
                      <p className="text-xs text-muted-foreground">Enter "native" for native blockchain token (ETH, BNB, etc.)</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Decimals</Label>
                      <Input type="number" placeholder="18" value={tokenForm.decimals} onChange={(e) => setTokenForm({ ...tokenForm, decimals: e.target.value })} required data-testid="token-decimals-input" />
                    </div>
                    <Button type="submit" className="w-full bg-primary text-white" disabled={saving} data-testid="save-token-btn">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingToken ? 'Update Token' : 'Add Token'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : (
              <Card className="border-border bg-card">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {tokens.map((token) => (
                      <div key={token.id} className="p-4 bg-card border border-gray-200 dark:border-white/10 rounded-lg shadow-sm" data-testid={`token-card-${token.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center"><Coins className="h-4 w-4 text-primary" /></div>
                            <div><p className="font-semibold">{token.symbol}</p><p className="text-xs text-muted-foreground">{token.name}</p></div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => toggleToken(token.id)}><Power className={`h-3 w-3 ${token.is_active !== false ? 'text-emerald-500' : 'text-muted-foreground'}`} /></Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openTokenDialog(token)}><Pencil className="h-3 w-3" /></Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => deleteToken(token.id)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </div>
                        <div className="space-y-1 text-xs">
                          <p className="text-muted-foreground">Network: <span className="text-foreground">{getNetworkName(token.network_id)}</span></p>
                          <p className="text-muted-foreground">Contract: <span className="font-mono">{token.contract_address === 'native' ? 'Native' : `${token.contract_address.slice(0, 10)}...`}</span></p>
                          <p className="text-muted-foreground">Decimals: {token.decimals}</p>
                        </div>
                        <Badge className={`mt-3 ${token.is_active !== false ? 'bg-emerald-500/20 text-emerald-500' : 'bg-muted text-muted-foreground'}`}>
                          {token.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    ))}
                    {tokens.length === 0 && (
                      <div className="col-span-full text-center py-8 text-muted-foreground">
                        <Coins className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No tokens configured</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminNetworks;
